import type { FC, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import {
  Box,
  Typography,
  TextField,
  Grid,
  Button,
  Card,
  CardContent,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  CreditCard as CreditCardIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { updateIntake, saveIntakeAsync, selectOnboarding } from '../../../store/onboardingSlice';
import { IntakeData } from '../../../api/marketing';

interface IntegrationsStepProps {
  onNext: () => void;
  onPrev: () => void;
}

interface FeatureToggle {
  id: keyof NonNullable<IntakeData['featuresRequested']>;
  label: string;
  description: string;
  icon: ReactNode;
}

const IntegrationsStep: FC<IntegrationsStepProps> = ({ onNext, onPrev }) => {
  const dispatch = useAppDispatch();
  const { intake } = useAppSelector(selectOnboarding);

  const [features, setFeatures] = useState<NonNullable<IntakeData['featuresRequested']>>({
    voip: intake.featuresRequested?.voip ?? true,
    email: intake.featuresRequested?.email ?? true,
    sms: intake.featuresRequested?.sms ?? false,
    creditBureau: intake.featuresRequested?.creditBureau ?? false,
    skipTracing: intake.featuresRequested?.skipTracing ?? false,
  });

  const { control, handleSubmit, watch } = useForm<Partial<IntakeData>>({
    defaultValues: {
      estimatedUsers: intake.estimatedUsers || 1,
      estimatedAccounts: intake.estimatedAccounts || 0,
      dataMigrationNeeded: intake.dataMigrationNeeded || false,
      currentSoftware: intake.currentSoftware || '',
    },
  });

  const formValues = watch();

  // Auto-save on change
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      dispatch(updateIntake({
        ...formValues,
        featuresRequested: features,
      }));
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [formValues, features, dispatch]);

  const featureToggles: FeatureToggle[] = [
    {
      id: 'voip',
      label: 'VoIP Phone Integration',
      description: 'SIP.js softphone with predictive dialer and call recording',
      icon: <PhoneIcon />,
    },
    {
      id: 'email',
      label: 'Email Integration',
      description: 'Send emails directly from Debtornator with templates',
      icon: <EmailIcon />,
    },
    {
      id: 'sms',
      label: 'SMS Messaging',
      description: 'Send text messages via Twilio with consent tracking',
      icon: <SmsIcon />,
    },
    {
      id: 'creditBureau',
      label: 'Credit Bureau Reporting',
      description: 'Report to Equifax, Experian, and TransUnion',
      icon: <CreditCardIcon />,
    },
    {
      id: 'skipTracing',
      label: 'Skip Tracing',
      description: 'Find updated contact information for debtors',
      icon: <SearchIcon />,
    },
  ];

  const handleFeatureToggle = (featureId: keyof NonNullable<IntakeData['featuresRequested']>) => {
    setFeatures((prev) => ({
      ...prev,
      [featureId]: !prev[featureId],
    }));
  };

  const onSubmit = async (data: Partial<IntakeData>) => {
    const integrationData = {
      ...data,
      featuresRequested: features,
    };
    dispatch(updateIntake(integrationData));
    await dispatch(saveIntakeAsync({ ...intake, ...integrationData, currentStep: 4 }));
    onNext();
  };

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)}>
      <Typography variant="h5" gutterBottom fontWeight={600}>
        Integration Preferences
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 4 }}>
        Select the features and integrations you'd like to enable.
      </Typography>

      {/* Feature Toggles */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          Features & Integrations
        </Typography>
        <Grid container spacing={2}>
          {featureToggles.map((feature) => (
            <Grid item xs={12} md={6} key={feature.id}>
              <Card
                variant="outlined"
                sx={{
                  cursor: 'pointer',
                  borderColor: features[feature.id] ? 'primary.main' : 'divider',
                  bgcolor: features[feature.id] ? 'rgba(100, 181, 246, 0.08)' : 'transparent',
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: 'primary.main',
                  },
                }}
                onClick={() => handleFeatureToggle(feature.id)}
              >
                <CardContent sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: features[feature.id] ? 'primary.main' : 'action.hover',
                      color: features[feature.id] ? 'white' : 'text.secondary',
                    }}
                  >
                    {feature.icon}
                  </Box>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {feature.label}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {feature.description}
                    </Typography>
                  </Box>
                  <Switch
                    checked={features[feature.id]}
                    onChange={() => handleFeatureToggle(feature.id)}
                    onClick={(e) => e.stopPropagation()}
                  />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Capacity Planning */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          Capacity Planning
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Controller
              name="estimatedUsers"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  type="number"
                  label="Estimated Number of Users"
                  inputProps={{ min: 1 }}
                  helperText="How many people will use Debtornator?"
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Controller
              name="estimatedAccounts"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  type="number"
                  label="Estimated Number of Accounts"
                  inputProps={{ min: 0 }}
                  helperText="Approximate number of debt accounts to manage"
                />
              )}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Data Migration */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          Data Migration
        </Typography>
        <Controller
          name="dataMigrationNeeded"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={
                <Switch
                  checked={field.value}
                  onChange={field.onChange}
                />
              }
              label="I need to migrate data from another system"
            />
          )}
        />

        {formValues.dataMigrationNeeded && (
          <Controller
            name="currentSoftware"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Current Software"
                placeholder="What system are you migrating from?"
                sx={{ mt: 2 }}
              />
            )}
          />
        )}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button
          variant="outlined"
          size="large"
          onClick={onPrev}
          startIcon={<ArrowBackIcon />}
        >
          Back
        </Button>
        <Button
          type="submit"
          variant="contained"
          size="large"
          endIcon={<ArrowForwardIcon />}
        >
          Continue
        </Button>
      </Box>
    </Box>
  );
};

export default IntegrationsStep;
